// src/blue-green/index.ts
var BLUE = "blue";
var GREEN = "green";
function normaliseColour(value, fallback) {
  const v = String(value ?? "").toLowerCase();
  if (v === BLUE) return BLUE;
  if (v === GREEN) return GREEN;
  if (v === "true" || v === "1") return BLUE;
  if (v === "false" || v === "0") return GREEN;
  return fallback;
}
function normaliseServices(value) {
  const out = {};
  if (value && typeof value === "object" && !Array.isArray(value)) {
    for (const [service, colour] of Object.entries(value)) {
      if (service) out[service] = normaliseColour(colour, GREEN);
    }
  }
  return out;
}
var BlueRoutingConfig = class _BlueRoutingConfig {
  default;
  services;
  constructor(deflt = GREEN, services = {}) {
    this.default = deflt;
    this.services = { ...services };
  }
  /** Resolve the effective colour for `service` (override else default). */
  colourFor(service) {
    return this.services[service] ?? this.default;
  }
  /** True when `service` resolves to the blue (latest) deploy. */
  isBlueFor(service) {
    return this.colourFor(service) === BLUE;
  }
  /** True when every service resolves to blue (no green overrides). */
  isGlobalBlue() {
    return this.default === BLUE && !Object.values(this.services).includes(GREEN);
  }
  /** True when nothing routes to blue (the normal, non-QA state). */
  isAllGreen() {
    return this.default === GREEN && !Object.values(this.services).includes(BLUE);
  }
  /** Service names explicitly pinned to a given colour. */
  servicesWithColour(colour) {
    return Object.keys(this.services).filter((s) => this.services[s] === colour);
  }
  /** Plain-object form for persistence / the JWT claim / equality checks. */
  toJSON() {
    return { default: this.default, services: { ...this.services } };
  }
  /** A fresh "everything green" config (the normal, non-QA state). */
  static allGreen() {
    return new _BlueRoutingConfig(GREEN, {});
  }
  /** A fresh "everything blue" config (global blue). */
  static globalBlue() {
    return new _BlueRoutingConfig(BLUE, {});
  }
  /**
   * Coerce a stored/claimed value into the structured shape.
   *
   * Tolerant of the LEGACY flat service-whitelist (`string[]`, where an empty
   * list meant "everything blue") so a pre-prod cutover and any in-flight tokens
   * keep working. Matches the Python `BlueRoutingConfig.from_value`:
   *
   *   - `null` / `{}` / `[]`               → `default=blue` (legacy empty = all blue)
   *   - `["authentication", "permissions"]`→ `default=green` + those services blue
   *   - `{ default, services }`            → structured config
   *   - bare `{ service: colour }` map     → `default=green` + that map
   */
  static fromValue(value) {
    if (value === null || value === void 0) return _BlueRoutingConfig.globalBlue();
    if (value instanceof _BlueRoutingConfig) return value;
    if (Array.isArray(value)) {
      const services = {};
      for (const item of value) if (item) services[String(item)] = BLUE;
      if (Object.keys(services).length === 0) return _BlueRoutingConfig.globalBlue();
      return new _BlueRoutingConfig(GREEN, services);
    }
    if (typeof value === "object") {
      const obj = value;
      if (Object.keys(obj).length === 0) return _BlueRoutingConfig.globalBlue();
      if ("default" in obj || "services" in obj) {
        return new _BlueRoutingConfig(
          normaliseColour(obj.default, GREEN),
          normaliseServices(obj.services)
        );
      }
      return new _BlueRoutingConfig(GREEN, normaliseServices(obj));
    }
    return _BlueRoutingConfig.allGreen();
  }
};
function routingFromQaConfig(qa) {
  if (!qa) return BlueRoutingConfig.allGreen();
  const cfg = qa.config;
  if (cfg && typeof cfg === "object" && !Array.isArray(cfg)) {
    return BlueRoutingConfig.fromValue(cfg);
  }
  if (qa.environment !== BLUE) return BlueRoutingConfig.allGreen();
  return BlueRoutingConfig.fromValue(cfg ?? []);
}

// src/flags/index.ts
var DEFAULT_OVERRIDE_KEY = "authe:flag-overrides";
var FeatureFlags = class {
  defaults;
  claims;
  routing;
  overrideKey;
  getStorage;
  constructor(init = {}) {
    this.defaults = init.defaults ?? {};
    this.claims = init.claims ?? {};
    this.routing = routingFromQaConfig(init.qaConfig ?? null);
    this.overrideKey = init.overrideStorageKey ?? DEFAULT_OVERRIDE_KEY;
    this.getStorage = init.getStorage ?? (() => typeof localStorage !== "undefined" ? localStorage : void 0);
  }
  overrides() {
    try {
      return JSON.parse(this.getStorage()?.getItem(this.overrideKey) ?? "{}");
    } catch {
      return {};
    }
  }
  /** Effective flag value: override > claim > default > false. */
  isEnabled(name) {
    const fromOverride = this.overrides()[name];
    if (fromOverride !== void 0) return fromOverride;
    const fromClaim = this.claims[name];
    if (fromClaim !== void 0) return fromClaim;
    const fromDefault = this.defaults[name];
    if (fromDefault !== void 0) return fromDefault;
    return false;
  }
  /** Set a local override (persists in storage). Pass `null` to clear it. */
  setOverride(name, value) {
    const storage = this.getStorage();
    if (!storage) return;
    const overrides = this.overrides();
    if (value === null) delete overrides[name];
    else overrides[name] = value;
    storage.setItem(this.overrideKey, JSON.stringify(overrides));
  }
  all() {
    return { ...this.defaults, ...this.claims, ...this.overrides() };
  }
  // ---- QA mode -----------------------------------------------------------
  /** The structured blue/green routing config resolved from the `qa_config` claim. */
  routingConfig() {
    return this.routing;
  }
  /**
   * Coarse QA environment label: `"blue"` when ANY service routes to the latest
   * deploy, else `"green"`. Prefer {@link colourFor}/{@link isBlueFor} for
   * per-service decisions.
   */
  qaEnvironment() {
    return this.routing.isAllGreen() ? "green" : "blue";
  }
  /** True when blue routing is active for at least one service. */
  isBlue() {
    return !this.routing.isAllGreen();
  }
  /** The resolved deployment colour for a given service (override else default). */
  colourFor(service) {
    return this.routing.colourFor(service);
  }
  /** Whether blue routing applies to a given service. */
  isBlueFor(service) {
    return this.routing.isBlueFor(service);
  }
  /** Service names explicitly pinned to blue (empty when the default is blue). */
  blueServices() {
    return this.routing.servicesWithColour("blue");
  }
};

export { FeatureFlags };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map